import {
  ANIMATIONS,
  ANIMATION_LIST,
  CATEGORY_NAMES,
  OVERLAYS,
  PLACE_SPOTS,
  SCENE_H,
  SCENE_W,
  composeCharacter,
  composePlace,
  composePlaceFront,
  friendLook,
  frameAt,
  type AnimCategory,
  type CharacterLook,
} from "@squire/sprites";
import { bitmapToSurface, SurfaceCache } from "../render/bitmapCanvas.js";
import { FrameLoop } from "../system/loop.js";
import { h } from "./dom.js";

const SCALE = 4;

/** ?debug=anim: every animation in every body stage, playing live. */
export function mountGallery(root: HTMLElement, look: CharacterLook): () => void {
  const cache = new SurfaceCache(1200);
  const cells: { ctx: CanvasRenderingContext2D; anim: (typeof ANIMATION_LIST)[number]; stage: number }[] = [];
  const wrap = h("div", { class: "gallery" }, h("h1", {}, `Galeria animacji (${ANIMATION_LIST.length})`));
  const cats = Object.keys(CATEGORY_NAMES) as AnimCategory[];
  for (const cat of cats) {
    wrap.append(h("h2", { class: "cat" }, CATEGORY_NAMES[cat]));
    for (const anim of ANIMATION_LIST.filter((a) => a.category === cat)) {
      const row = h("div", { class: "row", "data-anim": anim.id });
      row.append(
        h(
          "div",
          { class: "pixel", style: { width: "13em", color: "#fbf6e6" } },
          anim.label,
          h("br"),
          h("small", {}, anim.id),
        ),
      );
      for (let stage = 0; stage < 5; stage++) {
        const c = h("canvas", { width: 48, height: 52 });
        c.style.width = `${48 * SCALE}px`;
        c.style.height = `${52 * SCALE}px`;
        const ctx = c.getContext("2d")!;
        ctx.imageSmoothingEnabled = false;
        cells.push({ ctx, anim, stage });
        row.append(h("figure", {}, c, h("figcaption", {}, `stadium ${stage + 1}`)));
      }
      wrap.append(row);
    }
  }
  root.append(wrap);
  const start = Date.now();
  const loop = new FrameLoop((now) => {
    for (const cell of cells) {
      const { index, frame } = frameAt(cell.anim, now - start);
      const key = `${cell.anim.id}|${cell.stage}|${index}`;
      const s = cache.get(key, () =>
        bitmapToSurface(
          composeCharacter(look, cell.stage, frame.pose, { readingGlasses: cell.anim.id === "read" }),
        ),
      );
      cell.ctx.clearRect(0, 0, 48, 52);
      cell.ctx.drawImage(s as CanvasImageSource, 0, 4);
      const ov = frame.pose.overlay;
      if (ov) {
        const o = cache.get(`ov|${ov}`, () => bitmapToSurface(OVERLAYS[ov]));
        cell.ctx.drawImage(o as CanvasImageSource, 29, 4);
      }
    }
  }, 10);
  loop.start();
  return () => {
    loop.stop();
    wrap.remove();
  };
}

/** ?debug=places: café, bar, park and office with the companion and a friend, playing live. */
export function mountPlaces(root: HTMLElement, look: CharacterLook): () => void {
  const cache = new SurfaceCache(200);
  const places = ["cafe", "bar", "park", "office"] as const;
  const wrap = h("div", { class: "gallery" }, h("h1", {}, "Miejsca"));
  const cells = places.map((place) => {
    const c = h("canvas", { width: SCENE_W, height: SCENE_H });
    c.style.width = `${SCENE_W * SCALE}px`;
    c.style.height = `${SCENE_H * SCALE}px`;
    const ctx = c.getContext("2d")!;
    ctx.imageSmoothingEnabled = false;
    wrap.append(h("h2", { class: "cat" }, place), c);
    return { place, ctx };
  });
  root.append(wrap);
  const friend = friendLook(7);
  const start = Date.now();
  const minutes = new Date().getHours() * 60 + new Date().getMinutes();
  const loop = new FrameLoop((now) => {
    for (const { place, ctx } of cells) {
      const bg = cache.get(`bg|${place}`, () => bitmapToSurface(composePlace(place, { minutes, seed: 3 })));
      ctx.drawImage(bg as CanvasImageSource, 0, 0);
      const spots = PLACE_SPOTS[place];
      const draw = (
        who: CharacterLook,
        key: string,
        spot: { x: number; y: number; anim: string },
        flip: boolean,
        dx = 0,
      ) => {
        const anim = ANIMATIONS[spot.anim]!;
        const { index, frame } = frameAt(anim, now - start);
        const s = cache.get(`${key}|${spot.anim}|${index}`, () =>
          bitmapToSurface(composeCharacter(who, 1, frame.pose)),
        ) as CanvasImageSource;
        const x = spot.x + dx;
        if (flip) {
          ctx.save();
          ctx.translate(x + 25, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(s, 0, spot.y - 46);
          ctx.restore();
        } else ctx.drawImage(s, x - 24, spot.y - 46);
        if (frame.pose.overlay) {
          const o = cache.get(`ov|${frame.pose.overlay}`, () =>
            bitmapToSurface(OVERLAYS[frame.pose.overlay!]),
          );
          ctx.drawImage(o as CanvasImageSource, x + 5, spot.y - 38 - OVERLAYS[frame.pose.overlay].h);
        }
      };
      const walk = place === "park" ? (((now - start) / 1000) * 12) % 90 : 0;
      draw(look, "me", spots.me, false, walk);
      if (spots.friend) draw(friend, "fr", spots.friend, place !== "park", walk);
      const front = composePlaceFront(place);
      if (front)
        ctx.drawImage(cache.get(`front|${place}`, () => bitmapToSurface(front)) as CanvasImageSource, 0, 0);
    }
  }, 10);
  loop.start();
  return () => {
    loop.stop();
    wrap.remove();
  };
}
